import { Link } from '@tanstack/react-router';
import { PackageCheck } from 'lucide-react';

import { Badge } from '@/components/Badge/Badge';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    Table,
    TableBody,
    TableCell,
    TableCellStack,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import {
    type ReturnStage,
    transactionsExportUrl,
    type TransactionsReport,
} from '@/features/reports/api';
import { TRANSACTIONS_COPY } from '@/features/reports/copy';
import { formatPounds } from '@/features/reports/format';
import { ReportCard } from '@/features/reports/parts';
import { type ReportPeriod } from '@/features/reports/periods';
import { MISMATCH_CHECKS, RETURN_CHECK, RETURN_SETTLEMENT } from '@/features/reports/tabs';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { exportFileName } from '@/features/reports/transactions/labels';
import { formatDate } from '@/lib/format';
import { cn } from '@/util/utils';

const COPY = TRANSACTIONS_COPY.returns;

/**
 * Transactions › Returns (ALS-86). Requested and Received are what is waiting now: the database
 * keeps no history of when an item changed status, so they say "current". Completed is the
 * credit settled in the period, VAT in; the Margin view deducts the same refunds before VAT, and
 * each says which it is (Franka, 22 Oct 2026). Every completed return names the credit note or
 * old-portal refund that settled it. One since go-live that nothing settled is Unmatched, in the
 * period it was completed, as a warning; one from before go-live was most likely refunded outside
 * the system and is listed apart, in no period and no total.
 */
export function ReturnsView({
    report,
    period,
}: {
    report: TransactionsReport;
    period: ReportPeriod;
}) {
    const { stages, settled, awaiting, beforeGoLive } = report.returns;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <StageTile
                    label={COPY.stages.requested}
                    when={COPY.current}
                    stage={stages.requested}
                />
                <StageTile
                    label={COPY.stages.received}
                    when={COPY.current}
                    stage={stages.received}
                />
                <StageTile
                    label={COPY.stages.completed}
                    when={COPY.inclVat}
                    stage={stages.completed}
                />
                <StageTile
                    label={COPY.stages.unmatched}
                    when={COPY.thisPeriod}
                    stage={stages.unmatched}
                    warning={stages.unmatched.count > 0}
                />
                <StageTile label={COPY.stages.total} when="" stage={stages.total} />
            </div>

            <ReportCard
                title={COPY.awaiting.title}
                subtitle={COPY.awaiting.subtitle}
                actions={
                    <ExportButton
                        url={transactionsExportUrl('Awaiting', period)}
                        fileName={exportFileName(['awaiting', period])}
                        disabled={awaiting.length === 0}
                    />
                }
            >
                {awaiting.length === 0 ? (
                    <EmptyState
                        icon={<PackageCheck />}
                        title={COPY.awaiting.empty.title}
                        body={COPY.awaiting.empty.body}
                    />
                ) : (
                    <Table stack region={COPY.awaiting.title} className="table-stack--wide-labels">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.awaiting.columns.order}</TableHead>
                                <TableHead>{COPY.awaiting.columns.product}</TableHead>
                                <TableHead>{COPY.awaiting.columns.status}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.awaiting.columns.amount}
                                </TableHead>
                                <TableHead>{COPY.awaiting.columns.check}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.awaiting.columns.days}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {awaiting.map((a) => (
                                <TableRow key={a.orderLineId}>
                                    <TableCell data-stack="title">
                                        <TableCellStack>
                                            {/* The order opens on its Items tab, where the item's status is set. */}
                                            <Link
                                                to="/orders/$orderId"
                                                params={{ orderId: String(a.orderId) }}
                                                className="font-medium whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                                            >
                                                {a.orderNo}
                                            </Link>
                                            <span className="text-xs">{a.client}</span>
                                        </TableCellStack>
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.awaiting.columns.product}
                                    >
                                        <TableCellStack>
                                            <span>{a.product}</span>
                                            {a.brand ? (
                                                <span className="text-xs">{a.brand}</span>
                                            ) : null}
                                        </TableCellStack>
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.awaiting.columns.status}
                                        className="md:whitespace-nowrap"
                                    >
                                        {COPY.awaiting.status[a.status]}
                                    </TableCell>
                                    <TableNum
                                        data-stack="meta"
                                        data-label={COPY.awaiting.columns.amount}
                                    >
                                        {formatPounds(a.amount)}
                                    </TableNum>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.awaiting.columns.check}
                                    >
                                        {/* The longest check is wider than a 320 card's value column, so
                                            on a phone it wraps rather than running off the card. */}
                                        <Badge
                                            size="sm"
                                            className="max-md:shrink max-md:rounded-md max-md:text-left max-md:whitespace-normal"
                                            variant={
                                                MISMATCH_CHECKS.includes(a.check)
                                                    ? 'warning'
                                                    : a.check === RETURN_CHECK.ReadyToCredit
                                                      ? 'success'
                                                      : 'secondary'
                                            }
                                        >
                                            {COPY.awaiting.checks[a.check]}
                                        </Badge>
                                    </TableCell>
                                    <TableNum
                                        data-stack="meta"
                                        data-label={COPY.awaiting.columns.days}
                                    >
                                        {a.daysSinceArrival ?? '–'}
                                    </TableNum>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>

            <ReportCard
                title={COPY.settled.title}
                subtitle={COPY.settled.subtitle(report.current.label)}
                actions={
                    <ExportButton
                        url={transactionsExportUrl('Returns', period)}
                        fileName={exportFileName(['returns', period])}
                        disabled={settled.length === 0}
                    />
                }
            >
                {settled.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.settled.empty}</p>
                ) : (
                    <Table stack region={COPY.settled.title} className="table-stack--wide-labels">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.settled.columns.order}</TableHead>
                                <TableHead>{COPY.settled.columns.product}</TableHead>
                                <TableHead>{COPY.settled.columns.settledBy}</TableHead>
                                <TableHead>{COPY.settled.columns.settledOn}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.settled.columns.amount}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {settled.map((s, i) => (
                                // A line the old portal refunded twice is two rows.
                                <TableRow key={`${s.orderLineId}-${i}`}>
                                    <TableCell data-stack="title">
                                        <TableCellStack>
                                            <Link
                                                to="/orders/$orderId"
                                                params={{ orderId: String(s.orderId) }}
                                                className="font-medium whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                                            >
                                                {s.orderNo}
                                            </Link>
                                            <span className="text-xs">{s.client}</span>
                                        </TableCellStack>
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.settled.columns.product}
                                    >
                                        <TableCellStack>
                                            <span>{s.product}</span>
                                            {s.brand ? (
                                                <span className="text-xs">{s.brand}</span>
                                            ) : null}
                                        </TableCellStack>
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.settled.columns.settledBy}
                                        className="md:whitespace-nowrap"
                                    >
                                        {s.settlement === RETURN_SETTLEMENT.Unmatched ? (
                                            <Badge size="sm" variant="warning">
                                                {COPY.settled.by.Unmatched}
                                            </Badge>
                                        ) : (
                                            <TableCellStack>
                                                <span>{COPY.settled.by[s.settlement]}</span>
                                                {s.reference ? (
                                                    <span className="text-xs">{s.reference}</span>
                                                ) : null}
                                            </TableCellStack>
                                        )}
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.settled.columns.settledOn}
                                        className="text-muted-foreground whitespace-nowrap"
                                    >
                                        {s.settledOn ? formatDate(s.settledOn) : '–'}
                                    </TableCell>
                                    <TableNum data-stack="status" className="font-medium">
                                        {formatPounds(s.amount)}
                                    </TableNum>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>

            <ReportCard
                title={COPY.beforeGoLive.title}
                subtitle={COPY.beforeGoLive.subtitle(beforeGoLive.length)}
                actions={
                    <ExportButton
                        url={transactionsExportUrl('BeforeGoLive', period)}
                        fileName={exportFileName(['beforegolive'])}
                        disabled={beforeGoLive.length === 0}
                    />
                }
            >
                <p className="text-muted-foreground pb-3 text-xs">{COPY.beforeGoLive.note}</p>
                {beforeGoLive.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.beforeGoLive.empty}</p>
                ) : (
                    <Table
                        stack
                        region={COPY.beforeGoLive.title}
                        className="table-stack--wide-labels"
                    >
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.beforeGoLive.columns.order}</TableHead>
                                <TableHead>{COPY.beforeGoLive.columns.product}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.beforeGoLive.columns.amount}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {beforeGoLive.map((s) => (
                                <TableRow key={s.orderLineId}>
                                    <TableCell data-stack="title">
                                        <TableCellStack>
                                            <Link
                                                to="/orders/$orderId"
                                                params={{ orderId: String(s.orderId) }}
                                                className="font-medium whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                                            >
                                                {s.orderNo}
                                            </Link>
                                            <span className="text-xs">{s.client}</span>
                                        </TableCellStack>
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.beforeGoLive.columns.product}
                                    >
                                        <TableCellStack>
                                            <span>{s.product}</span>
                                            {s.brand ? (
                                                <span className="text-xs">{s.brand}</span>
                                            ) : null}
                                        </TableCellStack>
                                    </TableCell>
                                    <TableNum data-stack="status" className="font-medium">
                                        {formatPounds(s.amount)}
                                    </TableNum>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>
        </>
    );
}

function StageTile({
    label,
    when,
    stage,
    warning = false,
}: {
    label: string;
    when: string;
    stage: ReturnStage;
    /** A gap someone has to close: an Unmatched return since go-live. */
    warning?: boolean;
}) {
    return (
        <div
            role="group"
            aria-label={label}
            data-warning={warning || undefined}
            className={cn(
                'bg-card border-border min-w-0 rounded-lg border px-4 py-3',
                warning && 'bg-warning-muted border-warning-muted-border',
            )}
        >
            <p className="text-label-md text-muted-foreground">
                {label}
                {when ? <span className="font-normal normal-case"> · {when}</span> : null}
            </p>
            <p data-slot="tile-value" className="mt-1 text-2xl font-semibold tracking-tight">
                {formatPounds(stage.value)}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">{COPY.items(stage.count)}</p>
        </div>
    );
}
